import React, { useState, useEffect } from 'react';
import {
  MessageSquare, CheckCircle2, Clock, ArrowRight, Plus, Calendar,
  UserCheck, AlertTriangle, Filter, ChevronDown, X,
} from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import PageHeader from '../components/layout/PageHeader';
import {
  getInterventions,
  TAG_LABELS, TAG_COLORS, STATUS_LABELS, STATUS_COLORS,
} from '../services/managerPerformanceService';
import { useSportsLang } from '../contexts/SportsLangContext';

const INTV_TAG_LABELS = {
  pt: { carga: 'Carga', metodologia: 'Metodologia', retorno: 'Retorno', nutricao: 'Nutrição', observacao: 'Observação' },
  en: { carga: 'Load', metodologia: 'Methodology', retorno: 'Return', nutricao: 'Nutrition', observacao: 'Observation' },
};

const INTV_STATUS_LABELS = {
  pt: { pending: 'Pendente', in_progress: 'Em Execução', completed: 'Concluída' },
  en: { pending: 'Pending', in_progress: 'In Progress', completed: 'Completed' },
};

const LABELS = {
  pt: {
    title: 'Intervenções',
    subtitle: 'Coordenação técnica entre áreas · Decisões e ações',
    badge: 'GESTÃO',
    btnNew: 'Nova Intervenção',
    tabAll: 'Todas',
    tabPending: 'Pendentes',
    tabInProgress: 'Em Execução',
    tabCompleted: 'Concluídas',
    statDecisions: 'Decisões Tomadas',
    statPending: 'Pendentes',
    statInProgress: 'Em Execução',
    statCompleted: 'Concluídas',
    decisionBadge: '✓ Decisão',
    markDecision: 'Marcar como Decisão',
    assignOwner: 'Atribuir Responsável',
    createEvent: 'Criar Evento',
    completedAt: 'Concluída em',
    commentSingle: 'comentário',
    commentPlural: 'comentários',
    emptyTitle: 'Nenhuma intervenção encontrada',
    emptySubtitle: 'Ajuste os filtros ou crie uma nova intervenção',
    filterTag: 'Tag',
  },
  en: {
    title: 'Interventions',
    subtitle: 'Technical coordination across areas · Decisions and actions',
    badge: 'MANAGEMENT',
    btnNew: 'New Intervention',
    tabAll: 'All',
    tabPending: 'Pending',
    tabInProgress: 'In Progress',
    tabCompleted: 'Completed',
    statDecisions: 'Decisions Made',
    statPending: 'Pending',
    statInProgress: 'In Progress',
    statCompleted: 'Completed',
    decisionBadge: '✓ Decision',
    markDecision: 'Mark as Decision',
    assignOwner: 'Assign Owner',
    createEvent: 'Create Event',
    completedAt: 'Completed on',
    commentSingle: 'comment',
    commentPlural: 'comments',
    emptyTitle: 'No interventions found',
    emptySubtitle: 'Adjust filters or create a new intervention',
    filterTag: 'Tag',
  },
};

// ─── Reusable ─────────────────────────────────────────────────────────────────

const SkeletonRows = ({ count = 4 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="h-24 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
    ))}
  </div>
);

// ─── Thread Card ──────────────────────────────────────────────────────────────

const ThreadCard = ({ intervention, onMarkDecision, onAssignOwner }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const tagStyle = TAG_COLORS[intervention.tag] || TAG_COLORS.observacao;
  const statusStyle = STATUS_COLORS[intervention.status] || STATUS_COLORS.pending;
  const isCompleted = intervention.status === 'completed';
  const tagLabel = (INTV_TAG_LABELS[lang] || INTV_TAG_LABELS.pt)[intervention.tag] || TAG_LABELS[intervention.tag];
  const statusLabel = (INTV_STATUS_LABELS[lang] || INTV_STATUS_LABELS.pt)[intervention.status] || STATUS_LABELS[intervention.status];

  return (
    <div
      className={`bg-white rounded-xl border p-4 transition-all ${isCompleted ? 'opacity-70' : 'hover:shadow-md'}`}
      style={{ borderColor: VUKZ.border }}
    >
      {/* Tags row */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: tagStyle.bg, color: tagStyle.text }}>
          {tagLabel}
        </span>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}>
          {statusLabel}
        </span>
        <span className="text-[9px] px-2 py-0.5 rounded-full border" style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}>
          {intervention.scope === 'category' ? '📁' : '👤'} {intervention.scope_name}
        </span>
        {intervention.decided && (
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#d1fae5', color: '#065f46' }}>
            {t.decisionBadge}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="text-sm font-extrabold mb-1 leading-snug" style={{ color: VUKZ.text }}>
        {intervention.title}
      </h3>

      {/* Action suggested */}
      <div className="flex items-start gap-2 mb-3 p-2.5 rounded-lg" style={{ backgroundColor: VUKZ.bg }}>
        <ArrowRight size={12} className="mt-0.5 flex-shrink-0" style={{ color: VUKZ.textLight }} />
        <p className="text-[10px] leading-relaxed" style={{ color: VUKZ.textLight }}>
          {intervention.action_suggested}
        </p>
      </div>

      {/* Meta */}
      <div className="flex items-center gap-3 mb-3 text-[9px]" style={{ color: VUKZ.textLight }}>
        <span className="flex items-center gap-1">
          <UserCheck size={10} /> {intervention.owner}
        </span>
        <span className="flex items-center gap-1">
          <Clock size={10} /> {intervention.created_at}
        </span>
        {intervention.comments > 0 && (
          <span className="flex items-center gap-1">
            <MessageSquare size={10} /> {intervention.comments} {intervention.comments > 1 ? t.commentPlural : t.commentSingle}
          </span>
        )}
        {intervention.completed_at && (
          <span className="flex items-center gap-1">
            <CheckCircle2 size={10} /> {t.completedAt} {intervention.completed_at}
          </span>
        )}
      </div>

      {/* Action buttons */}
      {!isCompleted && (
        <div className="flex items-center gap-2 pt-3 border-t" style={{ borderColor: VUKZ.border }}>
          {!intervention.decided && (
            <button
              onClick={() => onMarkDecision?.(intervention.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all hover:opacity-80"
              style={{ background: VUKZ_GRADIENT, color: 'white' }}
            >
              <CheckCircle2 size={11} /> {t.markDecision}
            </button>
          )}
          <button
            onClick={() => onAssignOwner?.(intervention.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all hover:bg-gray-50"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}
          >
            <UserCheck size={11} /> {t.assignOwner}
          </button>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all hover:bg-gray-50"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}
          >
            <Calendar size={11} /> {t.createEvent}
          </button>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Interventions Page
// ═══════════════════════════════════════════════════════════════════════════════

const InterventionsPage = () => {
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [tagFilter, setTagFilter] = useState('');
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const items = await getInterventions('all');
        setInterventions(items);
      } catch (err) {
        console.error('Error loading interventions:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const tabs = [
    { key: 'all', label: t.tabAll, count: interventions.length },
    { key: 'pending', label: t.tabPending, count: interventions.filter((i) => i.status === 'pending').length },
    { key: 'in_progress', label: t.tabInProgress, count: interventions.filter((i) => i.status === 'in_progress').length },
    { key: 'completed', label: t.tabCompleted, count: interventions.filter((i) => i.status === 'completed').length },
  ];

  const tagOptions = [...new Set(interventions.map((i) => i.tag))];

  const filtered = interventions.filter((i) => {
    if (activeTab !== 'all' && i.status !== activeTab) return false;
    if (tagFilter && i.tag !== tagFilter) return false;
    return true;
  });

  const handleMarkDecision = (id) => {
    // TODO: PATCH /api/vukz/manager/interventions/:id { decided: true }
    setInterventions((prev) =>
      prev.map((i) => (i.id === id ? { ...i, decided: true, decision_date: new Date().toISOString().slice(0, 10) } : i))
    );
  };

  const handleAssignOwner = (id) => {
    // TODO: Open modal to select owner + role
    alert('TODO: Modal para atribuir responsável');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">

        {/* Header */}
        <PageHeader
          title={t.title}
          subtitle={t.subtitle}
          badgeText={t.badge}
          rightSlot={
            <button
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-90"
              style={{ background: VUKZ_GRADIENT, color: 'white' }}
            >
              <Plus size={14} /> {t.btnNew}
            </button>
          }
          className="mb-6"
        />

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white rounded-xl border p-1 mb-4" style={{ borderColor: VUKZ.border }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all"
              style={{
                backgroundColor: activeTab === tab.key ? VUKZ.primary : 'transparent',
                color: activeTab === tab.key ? 'white' : VUKZ.textLight,
              }}
            >
              {tab.label}
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                style={{
                  backgroundColor: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : VUKZ.primaryLight,
                  color: activeTab === tab.key ? 'white' : VUKZ.textLight,
                }}
              >
                {tab.count}
              </span>
            </button>
          ))}

          {/* Tag filter */}
          <div className="ml-auto flex items-center gap-2">
            {tagFilter && (
              <button
                onClick={() => setTagFilter('')}
                className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full"
                style={{ backgroundColor: TAG_COLORS[tagFilter]?.bg, color: TAG_COLORS[tagFilter]?.text }}
              >
                {(INTV_TAG_LABELS[lang] || INTV_TAG_LABELS.pt)[tagFilter] || TAG_LABELS[tagFilter]} <X size={10} />
              </button>
            )}
            <div className="relative">
              <button
                className="flex items-center gap-1 text-[10px] font-bold px-2.5 py-1.5 rounded-lg border hover:bg-gray-50 transition-all"
                style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}
                onClick={() => {
                  const next = tagOptions[(tagOptions.indexOf(tagFilter) + 1) % (tagOptions.length + 1)];
                  setTagFilter(next || '');
                }}
              >
                <Filter size={10} /> Tag
              </button>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: t.statDecisions, value: interventions.filter((i) => i.decided).length, icon: CheckCircle2, color: VUKZ.success },
            { label: t.statPending, value: interventions.filter((i) => i.status === 'pending').length, icon: Clock, color: VUKZ.warning },
            { label: t.statInProgress, value: interventions.filter((i) => i.status === 'in_progress').length, icon: ArrowRight, color: '#2563eb' },
            { label: t.statCompleted, value: interventions.filter((i) => i.status === 'completed').length, icon: CheckCircle2, color: VUKZ.success },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border p-3 flex items-center gap-3" style={{ borderColor: VUKZ.border }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}10` }}>
                <s.icon size={14} style={{ color: s.color }} />
              </div>
              <div>
                <div className="text-lg font-black" style={{ color: VUKZ.text }}>{s.value}</div>
                <div className="text-[9px]" style={{ color: VUKZ.textLight }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Interventions list */}
        {loading ? (
          <SkeletonRows count={4} />
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border" style={{ borderColor: VUKZ.border }}>
            <MessageSquare size={32} className="mx-auto mb-3" style={{ color: VUKZ.textLight }} />
            <p className="text-sm font-bold" style={{ color: VUKZ.text }}>{t.emptyTitle}</p>
            <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>{t.emptySubtitle}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((intv) => (
              <ThreadCard
                key={intv.id}
                intervention={intv}
                onMarkDecision={handleMarkDecision}
                onAssignOwner={handleAssignOwner}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterventionsPage;
